import { z } from 'zod';
import { MEDIA_KINDS, MEDIA_STATUSES } from '../constants.ts';
import { idSchema, unixMsSchema } from './common.ts';

export const mediaKindSchema = z.enum(MEDIA_KINDS);
export const mediaStatusSchema = z.enum(MEDIA_STATUSES);

export const mediaSchema = z.object({
  id: idSchema,
  org_id: idSchema,
  kind: mediaKindSchema,
  status: mediaStatusSchema,
  filename: z.string().nullable(),
  mime: z.string().nullable(),
  size_bytes: z.number().int().nonnegative().nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  duration_ms: z.number().int().nonnegative().nullable(),
  src_url: z.string().url().nullable(), // for kind=url
  // Content Engine (docs/09 §B1): the first-party template a html_template slide was made
  // from, and the current editable field values. Null for non-template media. `fields_json`
  // is the PARSED field map (key → string) — an editor populates its form from it directly.
  template_id: z.string().nullable().optional(),
  fields_json: z.record(z.string(), z.string()).nullable().optional(),
  created_at: unixMsSchema,
  // Number of playlists referencing this media (for the CMS delete guard). Present
  // on list/detail responses; the resolver/player never need it.
  used_in: z.number().int().nonnegative().optional(),
  // Whether a generated thumbnail exists in R2 (docs/09 media thumbnails). Computed
  // from a single R2 head, so it is present on the DETAIL response only — list responses
  // omit it (a head-per-item is too slow) and the CMS falls back to the original there.
  has_thumb: z.boolean().optional(),
});
export type Media = z.infer<typeof mediaSchema>;

// POST /v1/media → request a presigned upload for image/video.
export const createMediaSchema = z.object({
  filename: z.string().min(1),
  mime: z.string().min(1),
  size_bytes: z.number().int().positive(),
  kind: z.enum(['image', 'video']),
});
export type CreateMedia = z.infer<typeof createMediaSchema>;

export const createMediaResponseSchema = z.object({
  media_id: idSchema,
  upload_url: z.string().url(),
});

// POST /v1/media/:id/complete → client confirms upload. Body is optional and only
// used to carry video metadata the server cannot cheaply extract (docs/02: the
// client grabs a frame + dimensions during upload). Image dims are parsed server-side.
export const completeMediaSchema = z.object({
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  duration_ms: z.number().int().positive().optional(),
});
export type CompleteMedia = z.infer<typeof completeMediaSchema>;

// POST /v1/media/url → register a web page as content.
export const createUrlMediaSchema = z.object({
  src_url: z.string().url(),
  name: z.string().min(1),
});
export type CreateUrlMedia = z.infer<typeof createUrlMediaSchema>;

// GET /v1/media query filters. Draft generation outputs (status=draft) are hidden from
// normal lists (docs/09 §B) unless include_drafts=1 is passed.
export const mediaListQuerySchema = z.object({
  kind: mediaKindSchema.optional(),
  q: z.string().optional(),
  cursor: idSchema.optional(),
  include_drafts: z
    .union([z.literal('1'), z.literal('true'), z.literal('0'), z.literal('false')])
    .optional()
    .transform((v) => v === '1' || v === 'true'),
});
